import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDocumentTitle, normalizeDetail, resolveRouteTitle } from '@/lib/route-titles';

/** Reports the record a detail page is showing. Pages never write the title themselves. */
type PublishDetail = (pathname: string, detail: string) => void;

/**
 * The pathname a detail was published for. A record name left behind by the
 * page we just navigated away from must never title the page we landed on.
 */
interface PublishedDetail {
  pathname: string;
  detail: string;
}

const DocumentTitleContext = createContext<PublishDetail | undefined>(undefined);

interface DocumentTitleProviderProps {
  children: React.ReactNode;
}

/**
 * Owns `document.title` for every route.
 *
 * `index.html` ships a static `<title>SteerFi</title>` and nothing ever
 * replaced it, so every page reported the same title: tabs, bookmarks and
 * history entries were indistinguishable, and a screen reader had no way to
 * name the page (WCAG 2.4.2 Page Titled).
 *
 * The effect below is the only place in the app that assigns `document.title`.
 * Detail pages publish the record they loaded into this provider's state
 * instead of writing the title themselves, because React runs child effects
 * before parent effects: a page that wrote its own title would be overwritten
 * by this provider whenever the record happened to be ready on the first
 * render, and its unmount cleanup would wipe the *next* page's title. Sending
 * the data upward turns that ordering from a hazard into the mechanism.
 */
export const DocumentTitleProvider: React.FC<DocumentTitleProviderProps> = ({ children }) => {
  const { pathname } = useLocation();
  const [published, setPublished] = useState<PublishedDetail | null>(null);

  // Filtered during render rather than cleared from an effect, so a stale
  // record name is gone the instant the pathname changes -- not a frame later.
  const detail = published?.pathname === pathname ? published.detail : null;
  const section = resolveRouteTitle(pathname);

  useEffect(() => {
    document.title = formatDocumentTitle(section, detail);
    // No cleanup: the next route's pass owns the next value, and resetting here
    // would race the page coming in.
  }, [section, detail]);

  // Stable identity, and a bail-out when nothing changed, so a page can publish
  // from an effect without looping.
  const publishDetail = useCallback<PublishDetail>((publishedFor, next) => {
    setPublished((prev) =>
      prev?.pathname === publishedFor && prev.detail === next
        ? prev
        : { pathname: publishedFor, detail: next }
    );
  }, []);

  return (
    <DocumentTitleContext.Provider value={publishDetail}>{children}</DocumentTitleContext.Provider>
  );
};

/**
 * Folds the record a detail page is showing into `document.title`, e.g.
 * `Acme Corp — Clients — SteerFi`, so two clients produce two distinguishable
 * history entries (WCAG 2.4.2 Page Titled).
 *
 * Pass `null`/`undefined` while the record is loading; the route's own section
 * title stands in until then, so there is nothing to suppress. Call this with
 * the page's other hooks, above its loading and not-found early returns --
 * hooks cannot be conditional.
 */
export const useDocumentTitle = (detail: string | null | undefined): void => {
  const publishDetail = useContext(DocumentTitleContext);
  if (publishDetail === undefined) {
    throw new Error('useDocumentTitle must be used within a DocumentTitleProvider');
  }

  const { pathname } = useLocation();
  const normalized = normalizeDetail(detail);
  const lastSeen = useRef({ pathname, normalized });

  useEffect(() => {
    const movedRoute = lastSeen.current.pathname !== pathname;
    const movedRecord = lastSeen.current.normalized !== normalized;
    lastSeen.current = { pathname, normalized };

    // Nothing to publish while the record loads -- the route's own section title
    // is already in place. Deliberately does not publish the absence: coming
    // back to a detail page we have titled before, the name is still on record
    // for this pathname and shows immediately, and clearing it here would blank
    // it for the width of the refetch, flickering the tab title.
    if (normalized === null) return;

    // The route moved on but the page is still holding the record it read for
    // the previous one -- it was not remounted, and the new request has not
    // landed. Publishing now would title /clients/2 with client 1's name, and
    // leave it there for good if that request fails. Today `AppLayout` keys
    // `PageTransition` on the pathname, which remounts the page and hides this;
    // correctness should not rest on another component's `key`.
    //
    // The cost is that two records in a row with identical names, on a page
    // that does not remount, keep the section title rather than the name. A
    // missing name beats a confidently wrong one.
    if (movedRoute && !movedRecord) return;

    publishDetail(pathname, normalized);
  }, [publishDetail, pathname, normalized]);
};
